import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Award, Plus, Download, Trash2, X, Shield, FileText, Ban, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const TYPE_CONFIG = {
  completion: { label: 'Finalización', color: '#10B981', icon: '🏁' },
  commissioning: { label: 'Comisionamiento', color: '#3B82F6', icon: '⚡' },
  warranty: { label: 'Garantía', color: '#8B5CF6', icon: '🛡️' },
  inspection: { label: 'Inspección', color: '#F59E0B', icon: '🔍' },
  quality: { label: 'Calidad', color: '#06B6D4', icon: '✓' },
  safety: { label: 'Seguridad', color: '#EF4444', icon: '⚠️' },
};

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: '#64748B' },
  issued: { label: 'Emitido', color: '#3B82F6' },
  signed: { label: 'Firmado', color: '#10B981' },
  revoked: { label: 'Revocado', color: '#EF4444' },
};

export default function Certificates() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    type: 'completion', name: '', description: '', expiryDate: '',
    templateData: { installer: '', panelModel: '', inverterModel: '', panelCount: '', stringCount: '', warrantyYears: '', clientName: '', bodyText: '' },
  });

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCerts(); }, [selectedProject]);

  const loadCerts = () => {
    const params = {};
    if (selectedProject) params.project_id = selectedProject;
    api.get('/certificates', { params }).then(r => setCertificates(r.data)).catch(() => {});
  };

  const createCert = async () => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    if (!createForm.name) return toast.error('Nombre requerido');
    try {
      await api.post('/certificates', {
        projectId: selectedProject, type: createForm.type,
        name: createForm.name, description: createForm.description,
        expiryDate: createForm.expiryDate || null,
        templateData: createForm.templateData,
      });
      toast.success('Certificado creado');
      setShowCreate(false);
      setCreateForm({ type: 'completion', name: '', description: '', expiryDate: '',
        templateData: { installer: '', panelModel: '', inverterModel: '', panelCount: '', stringCount: '', warrantyYears: '', clientName: '', bodyText: '' } });
      loadCerts();
    } catch (e) { toast.error('Error'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/certificates/${id}`, { status });
      setCertificates(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      toast.success(`Estado: ${STATUS_CONFIG[status]?.label}`);
    } catch (e) { toast.error('Error'); }
  };

  const downloadPDF = async (id) => {
    try {
      const response = await api.get(`/certificates/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error generando PDF'); }
  };

  const deleteCert = async (id) => {
    if (!window.confirm('¿Eliminar certificado?')) return;
    try {
      await api.delete(`/certificates/${id}`);
      setCertificates(prev => prev.filter(c => c.id !== id));
      toast.success('Eliminado');
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="certificates-page">
      <div className="page-header">
        <div><h1><Award size={24} /> Certificados</h1><p className="subtitle">Generación y gestión de certificados</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Nuevo Certificado</button>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
      </div>

      <div className="cert-grid">
        {certificates.length === 0 && !loading && (
          <div className="empty-state"><Award size={48} /><h3>Sin certificados</h3></div>
        )}
        {certificates.map(cert => {
          const tc = TYPE_CONFIG[cert.type] || { label: cert.type, color: '#64748B', icon: '📄' };
          const sc = STATUS_CONFIG[cert.status] || STATUS_CONFIG.draft;
          return (
            <div key={cert.id} className="cert-card">
              <div className="cert-card-header">
                <span className="cert-type-icon">{tc.icon}</span>
                <div className="cert-card-info">
                  <span className="cert-name">{cert.name}</span>
                  <span className="cert-number">{cert.certificate_number}</span>
                </div>
                <span className="cl-badge" style={{ background: sc.color + '20', color: sc.color }}>{sc.label}</span>
              </div>
              <div className="cert-card-meta">
                <span>{tc.label}</span>
                <span>{cert.project_code}</span>
                <span>Emisión: {new Date(cert.issue_date).toLocaleDateString('es')}</span>
                {cert.expiry_date && <span>Expira: {new Date(cert.expiry_date).toLocaleDateString('es')}</span>}
              </div>
              {cert.description && <p className="cert-desc">{cert.description}</p>}
              <div className="cert-card-actions">
                <button className="btn btn-sm btn-primary" onClick={() => downloadPDF(cert.id)}>
                  <Download size={14} /> PDF
                </button>
                {cert.status === 'draft' && (
                  <button className="btn btn-sm btn-secondary" onClick={() => updateStatus(cert.id, 'issued')}>
                    <FileText size={14} /> Emitir
                  </button>
                )}
                {cert.status === 'issued' && (
                  <button className="btn btn-sm btn-success" onClick={() => updateStatus(cert.id, 'signed')}>
                    <Check size={14} /> Firmar
                  </button>
                )}
                {cert.status !== 'revoked' && cert.status !== 'draft' && (
                  <button className="btn btn-sm btn-danger" onClick={() => updateStatus(cert.id, 'revoked')}>
                    <Ban size={14} /> Revocar
                  </button>
                )}
                <button className="btn btn-sm btn-danger" onClick={() => deleteCert(cert.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header"><h3>Nuevo Certificado</h3><button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Proyecto *</label>
                  <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                    <option value="">Seleccionar...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo</label>
                  <select value={createForm.type} onChange={e => setCreateForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Nombre del certificado *</label>
                <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Certificado de Comisionamiento Planta Solar X" />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea rows={2} value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="Texto que aparece en el cuerpo del certificado" />
              </div>
              <div className="form-group">
                <label>Fecha de expiración</label>
                <input type="date" value={createForm.expiryDate} onChange={e => setCreateForm(f => ({ ...f, expiryDate: e.target.value }))} />
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '8px 0' }} />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Datos técnicos (opcionales)</p>
              <div className="form-row">
                <div className="form-group"><label>Instalador</label><input value={createForm.templateData.installer} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, installer: e.target.value } }))} /></div>
                <div className="form-group"><label>Nombre del cliente</label><input value={createForm.templateData.clientName} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, clientName: e.target.value } }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Modelo panel</label><input value={createForm.templateData.panelModel} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, panelModel: e.target.value } }))} /></div>
                <div className="form-group"><label>Modelo inversor</label><input value={createForm.templateData.inverterModel} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, inverterModel: e.target.value } }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Nº paneles</label><input type="number" value={createForm.templateData.panelCount} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, panelCount: e.target.value } }))} /></div>
                <div className="form-group"><label>Nº strings</label><input type="number" value={createForm.templateData.stringCount} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, stringCount: e.target.value } }))} /></div>
                <div className="form-group"><label>Años garantía</label><input type="number" value={createForm.templateData.warrantyYears} onChange={e => setCreateForm(f => ({ ...f, templateData: { ...f.templateData, warrantyYears: e.target.value } }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createCert}>Crear Certificado</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
